import React, { useState, useEffect } from 'react';
import { Delete, Lock, Eye, EyeOff, ShieldAlert, KeyRound, Loader2, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Logo from './Logo';

interface PinLockProps {
  savedPin: string | null;
  onSuccess: (pin: string) => void;
}

const PinLock: React.FC<PinLockProps> = ({ savedPin, onSuccess }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const isSetup = !savedPin;

  const handleNumberClick = (num: string) => {
    if (pin.length < 4) {
      setPin(prev => prev + num);
      setError(false);
      if ('vibrate' in navigator) navigator.vibrate(20);
    }
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    if ('vibrate' in navigator) navigator.vibrate(10);
  };

  const handleForgotPinReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error("Session expired.");

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: resetPassword
      });

      if (signInError) throw new Error("Incorrect account password.");

      const { error: updateError } = await supabase.from('user_profiles').update({ pin: null }).eq('id', user.id);
      if (updateError) throw updateError;
      
      window.location.reload();
    } catch (err: any) {
      setResetError(err.message);
    } finally {
      setIsResetting(false);
    }
  };

  useEffect(() => {
    if (pin.length === 4 && !isResetMode) {
      if (isSetup) {
        onSuccess(pin);
      } else {
        if (pin === savedPin) {
          onSuccess(pin);
        } else {
          setError(true);
          setPin('');
          if ('vibrate' in navigator) navigator.vibrate([100, 50, 100]);
        }
      }
    }
  }, [pin, savedPin, isSetup, onSuccess, isResetMode]);

  return (
    <div className="fixed inset-0 bg-[#0f172a] z-[100] flex flex-col items-center justify-start sm:justify-center p-6 text-white overflow-y-auto scrollbar-hide">
      <div className="absolute inset-0 opacity-10 fixed">
         <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2"></div>
         <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2"></div>
      </div>

      {!isResetMode ? (
        <div className="flex flex-col items-center w-full py-8 sm:py-0">
          <div className="mb-8 sm:mb-14 text-center relative z-10">
            <Logo size={80} className="mx-auto mb-6 sm:mb-8 drop-shadow-3xl" />
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-none text-white">
              {isSetup ? 'Set Your PIN' : 'Enter Your PIN'}
            </h2>
            <p className="text-amber-200/60 text-[10px] font-black uppercase tracking-[0.3em] mt-4 sm:mt-5">
              {isSetup ? 'Create a 4-digit PIN' : 'Enter your 4-digit PIN'}
            </p>
          </div>

          <div className="flex items-center gap-6 sm:gap-8 mb-10 sm:mb-16 relative z-10">
            <div className="flex gap-3 sm:gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-white/20 transition-all duration-500 relative flex items-center justify-center ${
                    pin.length > i ? 'bg-[#fbbf24] scale-125 sm:scale-150 shadow-[0_0_20px_rgba(251,191,36,0.5)] border-transparent' : 'bg-white/5'
                  } ${error ? 'border-red-400 bg-red-400 animate-pulse' : ''}`}
                >
                  {showPin && pin.length > i && (
                    <span className="text-[8px] font-black text-[#0f172a] absolute">{pin[i]}</span>
                  )}
                </div>
              ))}
            </div>
            <button 
              onClick={() => setShowPin(!showPin)}
              className="p-2.5 sm:p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white/30 hover:text-amber-400 transition-all border border-white/5"
            >
              {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-6 sm:gap-10 max-w-[280px] sm:max-w-xs w-full relative z-10">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                onClick={() => handleNumberClick(num.toString())}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-[2rem] bg-white/5 hover:bg-white/10 text-2xl sm:text-3xl font-black flex items-center justify-center backdrop-blur-xl transition-all active:scale-90 border border-white/5"
              >
                {num}
              </button>
            ))}
            <div />
            <button
              onClick={() => handleNumberClick('0')}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-[2rem] bg-white/5 hover:bg-white/10 text-2xl sm:text-3xl font-black flex items-center justify-center backdrop-blur-xl transition-all active:scale-90 border border-white/5"
            >
              0
            </button>
            <button
              onClick={handleDelete}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-[2rem] flex items-center justify-center text-white/20 hover:text-white transition-colors"
            >
              <Delete size={24} />
            </button>
          </div>

          <div className="mt-8 sm:mt-14 relative z-10 flex flex-col items-center gap-6">
            {error && (
              <p className="text-red-300 font-black text-[10px] uppercase tracking-widest flex items-center bg-red-500/20 px-8 py-4 rounded-full backdrop-blur-md border border-red-500/30">
                <Lock size={14} className="mr-3" /> Incorrect PIN
              </p>
            )}
            {!isSetup && (
              <button 
                onClick={() => setIsResetMode(true)}
                className="text-amber-200/40 hover:text-amber-400 font-black text-[10px] uppercase tracking-[0.2em] transition-colors"
              >
                Forgot Security PIN?
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="max-w-sm w-full bg-white/5 p-10 rounded-[3rem] border border-white/10 backdrop-blur-2xl relative z-10 animate-in fade-in zoom-in duration-300">
           <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-[1.2rem] bg-amber-400 flex items-center justify-center shadow-lg">
                <ShieldAlert className="text-[#0f172a]" />
              </div>
              <div>
                <h3 className="font-black text-white text-lg leading-tight uppercase tracking-tight">Reset PIN</h3>
                <p className="text-[10px] font-bold text-amber-200/60 uppercase tracking-widest">Confirm your password</p>
              </div>
           </div>

           <p className="text-white/60 text-xs font-bold leading-relaxed mb-8">
             To reset your secure PIN, please enter your main account password.
           </p>

           <form onSubmit={handleForgotPinReset} className="space-y-6">
              <div>
                <label className="block text-[9px] font-black text-white/40 uppercase tracking-widest mb-3 ml-1">Account Password</label>
                <div className="relative group">
                  <KeyRound className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-amber-400 transition-colors" size={18} />
                  <input 
                    type="password" 
                    required 
                    className="w-full pl-16 pr-6 py-5 bg-white/5 border border-white/5 rounded-[1.8rem] focus:bg-white/10 focus:border-white/20 outline-none text-white font-bold transition-all" 
                    placeholder="••••••••" 
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                  />
                </div>
              </div>

              {resetError && (
                <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-black uppercase text-center tracking-wider">
                  {resetError}
                </div>
              )}

              <button 
                type="submit" 
                disabled={isResetting}
                className="w-full bg-amber-400 text-[#0f172a] py-6 rounded-[2rem] font-black shadow-2xl flex items-center justify-center disabled:opacity-50 active:scale-95 transition-all"
              >
                {isResetting ? <Loader2 className="animate-spin" /> : 'Reset PIN'}
              </button>

              <button 
                type="button"
                onClick={() => { setIsResetMode(false); setResetError(''); setResetPassword(''); }} 
                className="w-full py-4 text-white/40 hover:text-white font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
              >
                <ArrowLeft size={14} /> Back to Lock Screen
              </button>
           </form>
        </div>
      )}
    </div>
  );
};

export default PinLock;
